/**
 * BUNDLE SWITCHER
 *
 * Swaps the product section in place when a bundle option is chosen, instead of
 * doing a full page navigation.
 *
 * Campaign pairs share one alternate template, so they use section_id + view
 * to render that configured section against either product. Regular pickers
 * retain full-page fetching to preserve different target template settings.
 *
 * Swapping the whole section element (not its children) brings along:
 *  - the target template's own block settings, so the picker is always the
 *    target page's own truth
 *  - the section-scoped <style> tags, whose selectors carry the new section id
 *  - the sticky add-to-cart and quick-buy, which render inside the section, so
 *    they always reflect the product now on screen
 *
 * The theme is web-component based, so inserting the fetched markup re-mounts
 * everything: `connectedCallback` fires for the incoming <product-gallery>,
 * <variant-picker>, <buy-buttons> etc. Insertion goes through
 * createContextualFragment so inline <script> tags in the section still run.
 *
 * Progressive enhancement: every option is a real <a href>. With JS off (or if
 * a fetch fails) the link navigates normally and the target page renders the
 * same picker with the correct option selected.
 */

(() => {
  if (window.__bundleSwitcher) return;
  window.__bundleSwitcher = true;

  const MAIN_SECTION = '.shopify-section--main-product';

  const CACHE = new Map(); // absolute url -> Promise<string>
  const CACHE_LIMIT = 12;

  function fetchPage(url) {
    const key = new URL(url, window.location.origin).toString();

    if (!CACHE.has(key)) {
      const request = fetch(key)
        .then((response) => {
          if (!response.ok) throw new Error(`Page fetch failed (${response.status}) for ${key}`);
          return response.text();
        })
        .catch((error) => {
          CACHE.delete(key); // don't cache failures, let the next attempt retry
          throw error;
        });

      CACHE.set(key, request);

      if (CACHE.size > CACHE_LIMIT) CACHE.delete(CACHE.keys().next().value);
    }

    return CACHE.get(key);
  }

  /** Replace the whole main product section with the fetched page's one. */
  function swapMainSection(doc) {
    const current = document.querySelector(MAIN_SECTION);
    const incoming = doc.querySelector(MAIN_SECTION);
    if (!current || !incoming) return false;

    current.replaceWith(document.createRange().createContextualFragment(incoming.outerHTML));
    return true;
  }

  /**
   * Extra sections (below-the-fold content that differs per bundle) are swapped
   * children-only, keeping the host element so section-scoped CSS and the
   * theme editor's section handles stay valid.
   */
  function swapExtraSection(doc, id) {
    const current = document.getElementById(`shopify-section-${id}`);
    const incoming = doc.getElementById(`shopify-section-${id}`);
    if (!current || !incoming) return false;

    current.replaceChildren(document.createRange().createContextualFragment(incoming.innerHTML));
    return true;
  }

  function syncBundleProof() {
    const productId = document.querySelector(`${MAIN_SECTION} bundle-switcher`)?.dataset.productId;
    if (!productId) return;

    document.querySelectorAll('[data-bundle-proof-product]').forEach((section) => {
      const hidden = section.dataset.bundleProofProduct !== productId;
      section.hidden = hidden;
      if (hidden) section.querySelectorAll('video').forEach((video) => video.pause());
    });
  }

  document.addEventListener('DOMContentLoaded', syncBundleProof);
  document.addEventListener('shopify:section:load', syncBundleProof);

  class BundleSwitcher extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      // Makes "is the switcher actually live on this page?" answerable at a
      // glance in the Elements panel, rather than by clicking and guessing.
      this.dataset.switcherReady = '';
      syncBundleProof();
      if (this.dataset.campaignView) {
        this.querySelectorAll('a[data-bundle-option][href]').forEach((option) => {
          option.href = this.navigationUrl(option.href);
        });
      }

      this.addEventListener('click', this.onClick.bind(this), { signal });
      this.addEventListener('pointerenter', this.onPreload.bind(this), { capture: true, signal });
      this.addEventListener('focusin', this.onPreload.bind(this), { signal });
      if (this.hasAttribute('data-visual-tabs')) {
        this.querySelectorAll('[role="tab"]').forEach((tab) => {
          tab.tabIndex = tab.hasAttribute('data-bundle-current') ? 0 : -1;
        });
        this.addEventListener('keydown', this.onTabKeyDown.bind(this), { signal });
      }

      // The section is re-rendered on every swap, so each new instance re-arms
      // its own popstate listener and the old one is aborted with the old node.
      window.addEventListener('popstate', this.onPopState.bind(this), { signal });

      if ('requestIdleCallback' in window) {
        this.idlePreload = requestIdleCallback(() => this.preloadAll(), { timeout: 2000 });
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      if (this.idlePreload !== undefined) window.cancelIdleCallback?.(this.idlePreload);
    }

    onTabKeyDown(event) {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      const index = tabs.indexOf(tab);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 :
        (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      tabs[next].focus();
      tabs[next].click();
    }

    navigationUrl(url) {
      const target = new URL(url, window.location.origin);
      if (this.dataset.campaignView) {
        target.searchParams.set('view', this.dataset.campaignView);
        const current = new URL(window.location.href);
        for (const [key, value] of current.searchParams) {
          if (key.startsWith('utm_') || ['gclid', 'fbclid', 'msclkid', 'ttclid', 'preview_theme_id'].includes(key)) {
            if (!target.searchParams.has(key)) target.searchParams.set(key, value);
          }
        }
      }
      return target.toString();
    }

    requestUrl(url) {
      const target = new URL(this.navigationUrl(url));
      if (this.dataset.campaignView && this.dataset.sectionId) {
        target.searchParams.set('section_id', this.dataset.sectionId);
      }
      return target.toString();
    }

    get extraSectionIds() {
      return (this.getAttribute('sync-sections') || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    }

    /* Switchable options only: the current one has nothing to fetch, and the
       builder option opens a modal rather than swapping product. */
    static SWITCHABLE = 'a[data-bundle-option][href]:not([data-bundle-current]):not([data-cb-open]):not([data-cb-target])';

    preloadAll() {
      if (!this.isConnected || navigator.connection?.saveData) return;
      this.querySelectorAll(BundleSwitcher.SWITCHABLE).forEach((option) => {
        fetchPage(this.requestUrl(option.href)).catch(() => {});
      });
    }

    onPreload(event) {
      const option = event.target.closest?.(BundleSwitcher.SWITCHABLE);
      if (!option) return;

      fetchPage(this.requestUrl(option.href)).catch(() => {});
    }

    onClick(event) {
      const option = event.target.closest('a[data-bundle-option]');
      if (!option) return;

      // "Build a bundle" opens the BYO modal — the global [data-cb-open] /
      // [data-cb-target] handler in cancha-bundle-embed owns that click.
      if (option.hasAttribute('data-cb-open') || option.hasAttribute('data-cb-target')) return;

      // Let modified clicks (new tab, download, middle click) behave natively.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      if (!option.href || option.hasAttribute('data-bundle-current')) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      this.switchTo(option.href);
    }

    onPopState(event) {
      if (!event.state?.bundleSwitcher) return;
      this.render(window.location.href, { updateUrl: false });
    }

    async switchTo(url) {
      // Stamp the entry we're leaving, otherwise going back to the product the
      // customer landed on hits a state-less entry and popstate can't restore it.
      if (!history.state?.bundleSwitcher) {
        history.replaceState({ ...history.state, bundleSwitcher: window.location.href }, '');
      }

      await this.render(this.navigationUrl(url), { updateUrl: true });
    }

    async render(url, { updateUrl = true } = {}) {
      // Guard against a slow first click resolving after a faster second one.
      const token = Symbol('render');
      BundleSwitcher.pending = token;

      this.setAttribute('aria-busy', 'true');
      const anchorTop = this.getBoundingClientRect().top;

      try {
        const html = await fetchPage(this.requestUrl(url));
        if (BundleSwitcher.pending !== token) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');

        const incoming = doc.querySelector(MAIN_SECTION);
        if (!incoming) throw new Error(`No ${MAIN_SECTION} in the page at ${url}`);
        if (this.dataset.campaignView && incoming.querySelector('bundle-switcher')?.dataset.campaignView !== this.dataset.campaignView) {
          throw new Error('Campaign template was not rendered');
        }
        if (updateUrl) history.pushState({ ...history.state, bundleSwitcher: url }, '', url);
        swapMainSection(doc);
        this.extraSectionIds.forEach((id) => swapExtraSection(doc, id));

        const pageTitle = doc.title || incoming.querySelector('bundle-switcher')?.dataset.pageTitle;
        if (pageTitle) document.title = pageTitle;

        this.restorePosition(anchorTop);
        document.dispatchEvent(new CustomEvent('bundle-switcher:change', { detail: { url } }));
      } catch (error) {
        if (BundleSwitcher.pending !== token) return;
        console.error('[Bundle switcher]', error);
        window.location.assign(url); // never leave the customer on a half-swapped page
      } finally {
        if (BundleSwitcher.pending === token) this.removeAttribute('aria-busy');
      }
    }

    /**
     * Bundle products often have a different number of gallery images, so the
     * content above the picker changes height. Re-anchor on the picker itself
     * and move focus to the newly selected option so the page doesn't jump
     * under the customer's cursor mid-decision. `this` is detached once the
     * section is replaced, so re-find the switcher inside the new section.
     */
    restorePosition(anchorTop) {
      const replacement = document.querySelector(`${MAIN_SECTION} bundle-switcher`);
      if (!replacement) return;

      const delta = replacement.getBoundingClientRect().top - anchorTop;
      if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: 'instant' });

      replacement.querySelector('a[data-bundle-current]')?.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('bundle-switcher')) {
    customElements.define('bundle-switcher', BundleSwitcher);
  }
})();
